import type {
  ReadoutClassification,
  ReadoutConfidenceTier,
  ReadoutMeasurementMethod,
} from '@/lib/readouts/readout-classification';

/**
 * Pure drafting logic behind `promote_workspace_learning`
 * (`supabase/migrations/20260826180000_promote_workspace_learning.sql`).
 *
 * Nothing here talks to Supabase — it mirrors `readout-classification.ts` and
 * `readout-generation.ts`'s split (pure mapping/decision layer, DB types stay
 * out) so the promotability rules are testable without a live database and
 * without importing the generated `Database` type.
 *
 * `workspace_learnings` enforces its OWN half of provenance at the DB layer:
 * an active learning requires an accepted source readout
 * (`trg_workspace_learnings_provenance`), and a claim must be 8–500 chars
 * (`workspace_learnings_claim_chk`). This module is the CLIENT-side half —
 * deciding whether a readout is even worth drafting a candidate from, and
 * building a claim that already satisfies the DB's own constraints, so a
 * human reviewer is never shown a candidate that the RPC would reject.
 *
 * Deliberately NOT encoded here: whether the source readout's `state` is
 * `'accepted'`. That is the trigger's job, and only the trigger's job — see
 * the "TWO JUDGES OF ONE STATE" note in the migration. Re-deriving that same
 * fact here would be a second, independently-maintained judge of it.
 */

export const LEARNING_TYPES = [
  'hook_pattern',
  'offer_pattern',
  'visual_pattern',
  'audience_pattern',
  'placement_pattern',
  'channel_role',
  'measurement_limitation',
  'brand_preference',
  'customer_language',
] as const;

export type LearningType = (typeof LEARNING_TYPES)[number];

/** The words a marketer reads for each type. The raw enum never reaches the screen. */
export const LEARNING_TYPE_LABEL: Record<LearningType, string> = {
  hook_pattern: 'Hook pattern',
  offer_pattern: 'Offer pattern',
  visual_pattern: 'Visual pattern',
  audience_pattern: 'Audience pattern',
  placement_pattern: 'Placement pattern',
  channel_role: 'Channel role',
  measurement_limitation: 'Measurement limitation',
  brand_preference: 'Brand preference',
  customer_language: 'Customer language',
};

export const LEARNING_CLAIM_MIN_LENGTH = 8;
export const LEARNING_CLAIM_MAX_LENGTH = 500;

const CLASSIFICATIONS_WITH_NO_REUSABLE_CLAIM: ReadonlySet<ReadoutClassification> = new Set([
  'inconclusive',
  'invalid_measurement',
]);

const MEASUREMENT_METHOD_LABEL: Record<ReadoutMeasurementMethod, string> = {
  deterministic_mora: 'Mora deterministic match',
  provider_reported: 'provider-reported',
  specialist_import: 'imported specialist attribution',
  observational: 'observational only',
  controlled_experiment: 'controlled experiment',
};

/**
 * The subset of an accepted `BriefReadout` this module needs. `dataFloorMet`
 * is not a top-level readout column — it lives inside `baseline_json`
 * (`readout-generation.ts`'s `GeneratedReadout.baseline.dataFloorMet`) — so
 * the caller extracts it before calling in, keeping this type DB-free.
 */
export type PromotableReadoutSummary = {
  briefId: string;
  decisionVersion: number;
  deliveryVersionVerified: boolean;
  classification: ReadoutClassification;
  confidenceTier: ReadoutConfidenceTier;
  dataFloorMet: boolean;
  primaryMetricKey: string | null;
  measurementMethod: ReadoutMeasurementMethod[];
  limitations: string[];
};

/**
 * `reasons` is present on BOTH branches, empty when promotable, rather than
 * living only on the refusal branch. A discriminated union would be tidier,
 * but every caller and test then depends on control-flow narrowing to reach
 * the reasons, and the one thing a refusal must never do is become hard to
 * read out.
 */
export type LearningPromotabilityResult = {
  promotable: boolean;
  reasons: string[];
};

/**
 * `inconclusive` and `invalid_measurement` readouts carry no reusable claim
 * by construction — the first found no directional result, the second found
 * its own comparison invalid. A readout whose data floor was not met is
 * never promotable either, independent of its classification: `classifyReadout`
 * downgrades an unmet floor straight to `inconclusive` in the honest case,
 * but a caller passing a stale or hand-built summary must not be able to
 * route around that by asserting a different classification while
 * `dataFloorMet` is still false.
 */
export function evaluateLearningPromotability(
  readout: PromotableReadoutSummary,
): LearningPromotabilityResult {
  const reasons: string[] = [];

  if (CLASSIFICATIONS_WITH_NO_REUSABLE_CLAIM.has(readout.classification)) {
    reasons.push(
      // The article agrees with the classification rather than being hardcoded:
      // the values are `inconclusive` / `supported` / `rejected` / `mixed`, and
      // "A 'inconclusive' readout" was rendering verbatim on the Brief page.
      `${/^[aeiou]/i.test(readout.classification) ? 'An' : 'A'} '${readout.classification}' readout carries no reusable claim and cannot be promoted.`,
    );
  }

  if (!readout.dataFloorMet) {
    reasons.push(
      'The readout did not meet its data floor, so no durable claim can be drawn from it.',
    );
  }

  if (!readout.deliveryVersionVerified) {
    reasons.push(
      'The readout is not verified against deliveries from this exact Brief decision version.',
    );
  }

  return { promotable: reasons.length === 0, reasons };
}

function describeMeasurementMethod(methods: ReadoutMeasurementMethod[]): string {
  if (methods.length === 0) return 'no measurement method recorded';
  return methods.map((method) => MEASUREMENT_METHOD_LABEL[method]).join(' + ');
}

function truncateToClaimLimit(claim: string): string {
  if (claim.length <= LEARNING_CLAIM_MAX_LENGTH) return claim;
  return `${claim.slice(0, LEARNING_CLAIM_MAX_LENGTH - 1).trimEnd()}…`;
}

/**
 * Builds the claim text carrying BOTH the evidence class (measurement
 * method) and the limitation text. A learning that dropped either would be
 * exactly the "guaranteed attribution" failure this product forbids — a
 * reusable claim presented with more certainty than the evidence it came
 * from actually supports.
 */
function buildClaimText(summarySentence: string, readout: PromotableReadoutSummary): string {
  const sentence = summarySentence.trim();
  const methodPhrase = describeMeasurementMethod(readout.measurementMethod);
  const limitationPhrase =
    readout.limitations.length > 0 ? ` Limitation: ${readout.limitations[0]}` : '';
  return truncateToClaimLimit(`${sentence} (${methodPhrase}).${limitationPhrase}`);
}

export type DraftLearningCandidateInput = PromotableReadoutSummary & {
  learningType: LearningType;
  /** The human- or model-proposed core sentence, e.g. "Hook A beat control on CTR." */
  summarySentence: string;
};

export type DraftedLearningCandidate = {
  learningType: LearningType;
  claim: string;
  /**
   * Carried through from the source readout verbatim, never upgraded. A
   * drafted claim may not present more confidence than the evidence it
   * came from actually earned.
   */
  confidenceTier: ReadoutConfidenceTier;
  evidenceSummary: {
    briefId: string;
    decisionVersion: number;
    classification: ReadoutClassification;
    measurementMethod: ReadoutMeasurementMethod[];
    primaryMetricKey: string | null;
    limitations: string[];
  };
};

export type DraftLearningCandidateResult = {
  ok: boolean;
  candidate: DraftedLearningCandidate | null;
  reasons: string[];
};

export function draftLearningCandidate(
  input: DraftLearningCandidateInput,
): DraftLearningCandidateResult {
  const promotability = evaluateLearningPromotability(input);
  if (!promotability.promotable) {
    return { ok: false, candidate: null, reasons: promotability.reasons };
  }

  if (input.summarySentence.trim().length === 0) {
    return {
      ok: false,
      candidate: null,
      reasons: ['A candidate claim needs a non-empty summary sentence.'],
    };
  }

  const claim = buildClaimText(input.summarySentence, input);

  if (claim.length < LEARNING_CLAIM_MIN_LENGTH || claim.length > LEARNING_CLAIM_MAX_LENGTH) {
    return {
      ok: false,
      candidate: null,
      reasons: [
        `Drafted claim is ${claim.length} characters, outside the table's ${LEARNING_CLAIM_MIN_LENGTH}–${LEARNING_CLAIM_MAX_LENGTH} range.`,
      ],
    };
  }

  return {
    ok: true,
    candidate: {
      learningType: input.learningType,
      claim,
      confidenceTier: input.confidenceTier,
      evidenceSummary: {
        briefId: input.briefId,
        decisionVersion: input.decisionVersion,
        classification: input.classification,
        measurementMethod: input.measurementMethod,
        primaryMetricKey: input.primaryMetricKey,
        limitations: input.limitations,
      },
    },
    reasons: [],
  };
}
